import React, { useCallback, useReducer } from 'react';

import _ from 'lodash';
import { Button, Checkbox, Dialog, DialogActions } from '@material-ui/core';
import { DialogContent, DialogTitle, FormControlLabel } from '@material-ui/core';
import { CircularProgress, Grid, IconButton } from '@material-ui/core';
import { FaCogs } from 'react-icons/fa';

import API from 'utils/api';
import { useSources } from 'hooks';
import { Tooltip } from 'components';
import { classes } from 'components/utils';

import styles from './GamePlaySettings.module.scss';

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOAD':
      return { loading: true };

    case 'SET_DATA':
      return { data: action.payload, open: !!action.payload };

    case 'SAVE':
      return { data: state.data, open: true, busy: true };

    case 'CLOSE':
      return { data: state.data, busy: action.payload };

    case 'CHECK': {
      const { name, checked } = action.payload;

      return {
        ...state,
        data: state.data.map(it => (it.name === name ? { name, checked } : it)),
      };
    }

    default:
      return state;
  }
};

const GamePlaySettings = ({ event }) => {
  const [state, dispatch] = useReducer(reducer, {});

  const sources = useSources();

  const fetchData = useCallback(
    async event => {
      let data;

      const eventId = _.get(event, '_id');
      const leagueId = _.get(event, 'game.leagueId');

      if (eventId && leagueId) {
        dispatch({ type: 'LOAD' });

        const res = await Promise.all([
          API.getPlayCategories(leagueId, sources),
          API.getChannelEvent(eventId, { select: 'broadcast' }, sources),
        ]);

        const categories = res[0].data || ['score', 'text'];
        const selectedCategories = _.get(res[1].data, 'broadcast.plays.categories') || [];

        data = categories.map(name => ({
          name,
          checked: selectedCategories.includes(name),
        }));
      }

      dispatch({ type: 'SET_DATA', payload: data });
    },
    [sources]
  );

  const save = useCallback(
    async (event, data) => {
      const eventId = _.get(event, '_id');

      if (eventId && data) {
        dispatch({ type: 'SAVE' });

        const categories = [];

        data.forEach(({ name, checked }) => {
          if (checked) {
            categories.push(name);
          }
        });

        await API.updateEventPlayCategories(eventId, categories, sources);
      }

      dispatch({ type: 'CLOSE', payload: true });
    },
    [sources]
  );

  return (
    <div className={styles.playSettings}>
      <div className={styles.header}>
        <IconButton
          color="primary"
          onClick={() => fetchData(event)}
          disabled={!!(state.loading || state.open)}
        >
          <Tooltip
            title="Select play categories to be automatically sent as chat messages"
            placement="top"
          >
            <FaCogs />
          </Tooltip>
        </IconButton>
      </div>

      <Dialog open={!!state.open}>
        <DialogTitle>
          Select play categories to be automatically sent as chat messages
        </DialogTitle>

        <DialogContent className={classes(styles.content, state.busy ? styles.busy : '')}>
          <Grid container className={styles.categories}>
            {state.data
              ? state.data.map(({ name, checked }) => (
                  <Grid item xs={4} key={name}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          color="primary"
                          onChange={e =>
                            dispatch({
                              type: 'CHECK',
                              payload: { name, checked: e.target.checked },
                            })
                          }
                        />
                      }
                      label={_.capitalize(name)}
                    />
                  </Grid>
                ))
              : null}
          </Grid>

          <div className={styles.overlay}>
            <CircularProgress />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => dispatch({ type: 'CLOSE' })} disabled={!!state.busy}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => save(event, state.data)}
            disabled={!!state.busy}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GamePlaySettings;
